import React, { CSSProperties, ReactElement, ReactNode, UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "../../../../../components/spiner/Spinner";
import styles from './bunchGrid.module.scss'

export interface IVirtualListProps<RowProps extends object> {
    children?: ReactNode;
    rowComponent: React.ComponentType<any>;
    rowCount: number;
    rowHeight: number | ((index: number) => number);
    rowProps: RowProps;
    overscanCount?: number;
    style?: CSSProperties;
    className?: string;
    activeIndex?: number;
    centerActiveItem?: boolean;
    onScroll?: (e: UIEvent<HTMLDivElement>) => void;
    loading?: boolean;
}

export function VirtualGrid<RowProps extends object>({
    children,
    rowComponent: RowComponent,
    rowCount,
    rowHeight,
    rowProps,
    overscanCount = 5,
    style,
    className,
    activeIndex,
    onScroll,
    loading
}: IVirtualListProps<RowProps>): ReactElement {

    const viewportRef = useRef<HTMLDivElement>(null);
    const rowsRef = useRef<Record<number, HTMLDivElement | null>>({});

    const [viewportHeight, setViewportHeight] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const itemHeight = typeof rowHeight === "function"
        ? rowHeight(0)
        : rowHeight;

    useEffect(() => {
        if (!viewportRef.current) return;

        setViewportHeight(viewportRef.current.clientHeight);

        const resizeObserver = new ResizeObserver(() => {
            if (viewportRef.current) {
                setViewportHeight(viewportRef.current.clientHeight);
            }
        });

        resizeObserver.observe(viewportRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (activeIndex === undefined || activeIndex < 0 || !viewportRef.current) return;

        const viewport = viewportRef.current;

        const elementTop = activeIndex * itemHeight;
        const elementBottom = elementTop + itemHeight;

        const viewportTop = viewport.scrollTop;
        const viewportBottom = viewport.scrollTop + viewport.clientHeight;

        if (elementTop < viewportTop) {
            viewport.scrollTo({
                top: elementTop,
            });
        } else if (elementBottom > viewportBottom) {
            viewport.scrollTo({
                top: elementBottom - viewport.clientHeight,
            });
        }
    }, [activeIndex, itemHeight]);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);

        onScroll?.(e);
    };

    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscanCount);
    const visibleCount = Math.ceil(viewportHeight / itemHeight) + overscanCount * 2;
    const endIndex = Math.min(rowCount, startIndex + visibleCount);

    const items = useMemo(() => {
        const result: JSX.Element[] = [];

        for (let i = startIndex; i < endIndex; i++) {
            result.push(
                <div
                    key={i}
                    ref={(el) => {
                        rowsRef.current[i] = el;
                    }}
                    style={{
                        position: "absolute",
                        top: i * itemHeight,
                        left: 0,
                        right: 0,
                        height: itemHeight,
                    }}
                >
                    <RowComponent
                        index={i}
                        style={{
                            width: "100%",
                            height: itemHeight,
                        }}
                        {...rowProps}
                    />
                </div>
            );
        }

        return result;
    }, [startIndex, endIndex, itemHeight, RowComponent, rowProps]);

    return (
        <div
            ref={viewportRef}
            className={className}
            style={{
                ...style,
                overflow: "auto",
                position: "relative",
                minHeight: viewportHeight || "200px",
            }}
            onScroll={handleScroll}
        >
            {children}
            {loading ? (
                <div
                    className={styles.loaderContainer}
                    style={{ top: `${rowHeight}px` }}
                >
                    <Spinner />
                </div>
            ) : (
                <div
                    style={{
                        position: "relative",
                        height: rowCount * itemHeight,
                    }}
                >
                    {items}
                </div>
            )}
        </div>
    );
}